import { CheckIcon, Label, Paragraph } from '@quickmd-llc/design-system'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { useSelector } from 'store/hooks/useSelector'
import { selectLiveVisitHdMockEnabled } from 'store/flags/FlagsSelectors'
import { getIntakeForm } from 'store/charts/ChartsThunk'
import { useGetPatientsPatientIdQuestionnairesSessionId } from 'api/generated/questionnaire/questionnaire'
import { QuestionTypeSelector } from 'pages/charts/patient-chart/components/intake-questions/components/question-type-selector'
import { MOCK_LIVE_VISIT_MEDICATION_ANSWERS } from '../../mocks/prj578LiveVisitMocks'
import { VisitSidePanel } from './VisitSidePanel'
// The intake-form variant reuses the intake-questions page styling (`intake-questions-*` classes).
import 'pages/charts/patient-chart/components/intake-questions/IntakeQuestions.scss'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const selectIntakeForm = (state: Store) => state.chartsReducer.intakeForm
const selectIsIntakeLoading = (state: Store) => state.chartsReducer.loading

/**
 * Slides in from the left over the patient-info column to show a completed visit's responses —
 * the online-visit ("reviewed") questionnaire answers (async) or the intake-form answers — without
 * opening a new tab. Header is locked; the Q&A body scrolls. For the async variant, clicking a
 * question copies the question + answer to the clipboard (mirrors the note copy).
 */
export const VisitResponsesPanel = ({ isOpen, onClose }: Props) => {
  const { patientWithVisitInfo } = useDailyVisit()
  const dispatch = useDispatch()
  // Inline "Copied!" affordance per row (mirrors the note copy — no toast).
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    },
    [],
  )

  const isAsyncVisit = patientWithVisitInfo.VisitMethod === 'asynchronous'
  // PRJ-578: with the `live_visit_hd_mock` Dev Tools pin on, live (video) visits show made-up
  // medication Q&As (see prj578LiveVisitMocks), reusing the async questionnaire row shape. With the
  // pin off, which is the default and the only state where production is served, video visits
  // fetch and show their real intake form.
  const isLiveVisitMockEnabled = useSelector(selectLiveVisitHdMockEnabled)
  const isLiveVisit = isLiveVisitMockEnabled && patientWithVisitInfo.VisitMethod === 'video'
  const patientId = patientWithVisitInfo.PatientId
  const questionnaireSessionId = patientWithVisitInfo.Questionnaires?.[0]?.QuestionnaireSessionId
  const intakeFormId = patientWithVisitInfo.IntakeForm?.IntakeFormId

  const { data: questionnaireSession, isLoading: isQuestionnaireLoading } =
    useGetPatientsPatientIdQuestionnairesSessionId(patientId || '', questionnaireSessionId || '', {
      query: { enabled: isOpen && isAsyncVisit && Boolean(questionnaireSessionId) },
    })

  const intakeForm = useSelector(selectIntakeForm)
  const isIntakeLoading = useSelector(selectIsIntakeLoading)

  useEffect(() => {
    // With the live-visit mock on, video visits render the mocked Q&As, so skip the fetch.
    if (isOpen && !isAsyncVisit && !isLiveVisit && patientId && intakeFormId) {
      const action = getIntakeForm({ patientId, intakeFormId })
      if (action) dispatch(action)
    }
  }, [isOpen, isAsyncVisit, isLiveVisit, patientId, intakeFormId, dispatch])

  // Async and live visits both render questionnaire-style Q&A rows; only the source differs.
  const questionnaireAnswers =
    isAsyncVisit ? (questionnaireSession?.Answers ?? [])
    : isLiveVisit ? MOCK_LIVE_VISIT_MEDICATION_ANSWERS
    : []
  const showQuestionnaireRows = isAsyncVisit || isLiveVisit

  const submittedDate =
    isAsyncVisit ? patientWithVisitInfo.Questionnaires?.[0]?.CompleteDateTime
    : isLiveVisit ? undefined
    : patientWithVisitInfo.IntakeForm?.CompleteDateTime
  const title =
    isAsyncVisit ? 'Visit Responses'
    : isLiveVisit ? 'Medication questions'
    : intakeForm?.Name || 'Intake form'
  const isLoading =
    isAsyncVisit ? isQuestionnaireLoading
    : isLiveVisit ? false
    : isIntakeLoading

  const handleCopyAnswer = async (index: number, question?: string | null, answer?: string | null) => {
    const text = [question, answer].filter(Boolean).join('\n')
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopiedIndex(index)
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
      copiedTimeoutRef.current = setTimeout(() => setCopiedIndex(null), 2000)
    } catch {
      // Clipboard write can fail if the page isn't focused (mirrors the note copy).
    }
  }

  return (
    <VisitSidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      headerRight={
        submittedDate ?
          <Paragraph size='sm' className='text-text-light'>
            Submitted {moment(submittedDate).format('MM/DD/YYYY')}
          </Paragraph>
        : null
      }>
      {isLoading ?
        <Paragraph size='sm' className='text-text-light'>
          Loading…
        </Paragraph>
      : showQuestionnaireRows ?
        <div className='flex flex-col'>
          {questionnaireAnswers.map((answer, index) => (
            <button
              type='button'
              key={answer.Question}
              onClick={() => handleCopyAnswer(index, answer.Question, answer.Answer)}
              className='flex w-full cursor-pointer flex-col items-start gap-sp-0.5 border-b border-border-medium py-sp-1.5 text-left transition-colors last:border-b-0 hover:bg-coastal-blue-10'>
              <Label size='md' className='text-text-dark'>
                {answer.Question}
              </Label>
              <Paragraph size='sm' className='text-text-medium'>
                {answer.Answer}
              </Paragraph>
              {copiedIndex === index ?
                <span className='flex items-center gap-sp-0.5 text-highlight'>
                  <CheckIcon />
                  <Label size='sm' className='text-highlight'>
                    Copied to clipboard
                  </Label>
                </span>
              : null}
            </button>
          ))}
        </div>
      : <div className='intake-questions-table'>
          {(intakeForm?.Questions ?? []).map((question, index) => {
            // eslint-disable-next-line react/no-array-index-key -- intake questions are a static, non-reordering list with no stable id
            return <QuestionTypeSelector key={index} question={question} index={index} />
          })}
        </div>
      }
    </VisitSidePanel>
  )
}
